import React, { useState } from 'react';
import {
  Box,
  CssBaseline,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { CompanySidebar } from './CompanySidebar';
import { Footer } from './Footer';
import { useAuth } from '../../hooks/useAuth';

interface MainLayoutProps {
  children: React.ReactNode;
  title?: string;
  showSidebar?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
  footerVariant?: 'default' | 'minimal';
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  currentView?: 'welcome' | 'chat';
}

const SIDEBAR_WIDTH = 280;
const HEADER_HEIGHT = 64;

export const MainLayout: React.FC<MainLayoutProps> = ({
  children,
  title,
  showSidebar = true,
  showHeader = true,
  showFooter = true,
  footerVariant = 'minimal',
  maxWidth = 'lg',
  currentView,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const { user } = useAuth();
  const isCompanyUser = user?.userRole === 'company_admin' || user?.userRole === 'hr' || user?.userRole === 'employee';

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarClose = () => {
    setSidebarOpen(false);
  };

  // Calculate main content margins based on layout configuration
  const getMainContentStyles = () => {
    const baseStyles = {
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column' as const,
      minHeight: '100vh',
      pt: showHeader ? `${HEADER_HEIGHT}px` : 0,
      minWidth: 0,
    };

    if (!showSidebar || isMobile) {
      return baseStyles;
    }

    return {
      ...baseStyles,
      ml: `${SIDEBAR_WIDTH}px`,
      width: `calc(100% - ${SIDEBAR_WIDTH}px)`,
    };
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      
      {/* Header */}
      {showHeader && (
        <Header
          title={title}
          onMenuClick={handleSidebarToggle}
          showMenuButton={showSidebar}
        />
      )}

              {/* Sidebar */}
        {showSidebar && (
          isCompanyUser ? (
            <CompanySidebar
              open={sidebarOpen}
              onClose={handleSidebarClose}
              variant={isMobile ? 'temporary' : 'permanent'}
            />
          ) : (
            <Sidebar
              open={sidebarOpen}
              onClose={handleSidebarClose}
              variant={isMobile ? 'temporary' : 'permanent'}
              currentView={currentView}
              title={title}
            />
          )
        )}

      {/* Main Content Area */}
      <Box sx={getMainContentStyles()}>
        {/* Page Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, sm: 3 },
            maxWidth: maxWidth ? `${theme.breakpoints.values[maxWidth]}px` : 'none',
            mx: maxWidth ? 'auto' : 0,
            width: '100%',
            overflowX: 'hidden',
          }}
        >
          {children}
        </Box>

        {/* Footer */}
        {showFooter && <Footer variant={footerVariant} />}
      </Box>
    </Box>
  );
};

export default MainLayout;